import { computed, ref } from 'vue';
import { defineStore } from 'pinia';

export type ThemeMode = 'light' | 'dark' | 'system';

export interface PaletteData {
  key: string;
  name: string;
  tokens: {
    light: Record<string, string>;
    dark: Record<string, string>;
  };
}

/**
 * Subset av UserPreferences som themeStore.hydrateFromBackend leser.
 * Holdes lokal og strukturell (ikke importert fra UserPreferences-typen)
 * for å unngå sirkulære avhengigheter mellom store-laget og api-typer.
 */
export interface ThemePreferenceFragment {
  displayMode?: string | null;
}

/**
 * Save-callback for backend-persistens. Settes av composable-laget
 * (useUserPreferencesHydration) for å unngå sirkulær import mellom
 * stores/themeStore.ts og store/storeUserPreferences.js.
 */
export type DisplayModeSaveCallback = (mode: ThemeMode) => void | Promise<void>;

const STORAGE_KEY = 'ics-theme';
const TOKEN_PREFIX = '--color-';

/**
 * Loud amber failsafe-drakt (#1359). Brukes når /api/config ikke kan lastes ved
 * boot — da er BE/DB unåbar og vi kan ikke hente miljø-paletten fra serveren.
 * Speiler intensjonen til BE-paletten `ics-warning-unknown` (DbUp 0082): tydelig
 * amber som signaliserer feilkonfigurasjon, og som ALDRI forveksles med prod-blå.
 */
const UNAVAILABLE_PALETTE: PaletteData = {
  key: 'ics-config-unavailable',
  name: 'Config utilgjengelig (failsafe)',
  tokens: {
    light: {
      'color-bg-canvas': '255 250 235',
      'color-bg-surface': '255 252 240',
      'color-bg-muted': '254 245 215',
      'color-fg-default': '60 40 10',
      'color-fg-muted': '120 90 40',
      'color-fg-on-accent': '60 30 0',
      'color-border-default': '253 224 150',
      'color-border-strong': '217 119 6',
      'color-accent-default': '245 158 11',
      'color-accent-emphasis': '217 119 6',
      'color-accent-subtle': '254 240 195',
      'color-gradient-from': '245 158 11',
      'color-gradient-to': '217 119 6',
      'color-success': '16 185 129',
      'color-danger': '239 68 68',
      'color-warning': '217 119 6',
      'color-info': '59 130 246'
    },
    dark: {
      'color-bg-canvas': '45 32 10',
      'color-bg-surface': '60 45 18',
      'color-bg-muted': '75 58 25',
      'color-fg-default': '253 230 175',
      'color-fg-muted': '200 170 110',
      'color-fg-on-accent': '60 30 0',
      'color-border-default': '110 80 30',
      'color-border-strong': '217 119 6',
      'color-accent-default': '253 186 75',
      'color-accent-emphasis': '245 158 11',
      'color-accent-subtle': '80 55 20',
      'color-gradient-from': '180 110 20',
      'color-gradient-to': '130 75 10',
      'color-success': '52 211 153',
      'color-danger': '248 113 113',
      'color-warning': '251 191 36',
      'color-info': '96 165 250'
    }
  }
};

function loadStoredMode (): ThemeMode {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // localStorage may be disabled
  }
  return 'system';
}

function detectSystemPrefersDark (): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function isThemeMode (value: unknown): value is ThemeMode {
  return value === 'light' || value === 'dark' || value === 'system';
}

export const useThemeStore = defineStore('theme', () => {
  const palette = ref<PaletteData | null>(null);
  const mode = ref<ThemeMode>(loadStoredMode());
  const systemPrefersDark = ref<boolean>(detectSystemPrefersDark());
  const isHydrating = ref<boolean>(false);
  // #1359: true når miljø-config ikke kunne lastes og failsafe-drakten er aktiv.
  const unavailable = ref<boolean>(false);

  let mediaQuery: MediaQueryList | null = null;
  let mediaListener: ((e: MediaQueryListEvent) => void) | null = null;
  let saveCallback: DisplayModeSaveCallback | null = null;

  const effectiveMode = computed<'light' | 'dark'>(() => {
    if (mode.value === 'system') return systemPrefersDark.value ? 'dark' : 'light';
    return mode.value;
  });

  function applyTokens (): void {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    root.dataset.theme = effectiveMode.value;

    if (!palette.value) return;
    const tokens = palette.value.tokens[effectiveMode.value];
    if (!tokens) return;

    for (const [key, value] of Object.entries(tokens)) {
      const cssName = key.startsWith(TOKEN_PREFIX) ? key : `${TOKEN_PREFIX}${key.replace(/^color-/, '')}`;
      root.style.setProperty(cssName, value);
    }
  }

  function setPalette (next: PaletteData | null): void {
    palette.value = next;
    applyTokens();
  }

  /**
   * Aktiver loud amber failsafe-drakt (#1359) når miljø-config ikke kunne lastes.
   * Sikrer at skjermen aldri ser ut som prod når vi ikke vet hvilket miljø vi er i.
   */
  function setUnavailable (): void {
    unavailable.value = true;
    palette.value = UNAVAILABLE_PALETTE;
    applyTokens();
  }

  /**
   * Sett save-callback for backend-persistens. Kalles av composable-laget
   * etter at userPreferencesStore er klar.
   */
  function setSaveCallback (callback: DisplayModeSaveCallback | null): void {
    saveCallback = callback;
  }

  function persistLocal (next: ThemeMode): void {
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // localStorage may be disabled — accept memory-only mode
    }
  }

  function setMode (next: ThemeMode): void {
    mode.value = next;
    persistLocal(next);
    applyTokens();

    // Skipp backend-write under hydrering for å unngå loop
    // (hydrate → setMode → save → reload → hydrate ...).
    if (!isHydrating.value && saveCallback) {
      void saveCallback(next);
    }
  }

  function cycleMode (): void {
    const order: ThemeMode[] = ['light', 'dark', 'system'];
    const idx = order.indexOf(mode.value);
    setMode(order[(idx + 1) % order.length]);
  }

  /**
   * Hydrer mode fra backend-preferences. Backend wins hvis displayMode er satt;
   * fallback til eksisterende state (typisk localStorage warm-cache) hvis null.
   * Trigger ALDRI backend-write — bruker `isHydrating`-flagg for å låse ut
   * persistens under setMode-kallet.
   */
  function hydrateFromBackend (prefs: ThemePreferenceFragment | null | undefined): void {
    if (!prefs) return;

    isHydrating.value = true;
    try {
      const backendMode = prefs.displayMode;
      if (isThemeMode(backendMode)) {
        setMode(backendMode);
      } else {
        // Backend-verdi er null/ugyldig — fortsatt apply tokens slik at
        // data-theme reflekterer valgt warm-cache mode.
        applyTokens();
      }
    } finally {
      isHydrating.value = false;
    }
  }

  function startSystemListener (): void {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    if (mediaQuery) return;
    mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaListener = (e) => {
      systemPrefersDark.value = e.matches;
      if (mode.value === 'system') applyTokens();
    };
    mediaQuery.addEventListener('change', mediaListener);
  }

  function stopSystemListener (): void {
    if (mediaQuery && mediaListener) {
      mediaQuery.removeEventListener('change', mediaListener);
    }
    mediaQuery = null;
    mediaListener = null;
  }

  return {
    palette,
    mode,
    systemPrefersDark,
    isHydrating,
    unavailable,
    effectiveMode,
    applyTokens,
    setPalette,
    setUnavailable,
    setMode,
    cycleMode,
    hydrateFromBackend,
    setSaveCallback,
    startSystemListener,
    stopSystemListener
  };
});
